import React, { useState, useEffect, useRef } from 'react';
import { Product, ICONS_MAP } from '@/app/data';
import { ArrowUpRight } from 'lucide-react';
import { clsx } from 'clsx';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
  onClick: (product: Product) => void;
}

const getCategoryColor = (category: string) => {
  const lowerCat = category.toLowerCase();
  if (lowerCat.includes('city') || lowerCat.includes('urbano')) return '#061970';
  if (lowerCat.includes('work') || lowerCat.includes('trabajo')) return '#FF7F32';
  if (lowerCat.includes('road') || lowerCat.includes('carretera') || lowerCat.includes('deportivo')) return '#459EDF';
  return '#061970'; // Default to Navy
};

export const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cycle through images on hover
  useEffect(() => {
    if (isHovering && product.images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      }, 800); // Change image every 800ms
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setCurrentImageIndex(0); // Reset to first image when not hovering
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, product.images.length]);
  // Use first 2 features for tags
  const activeFeatures = Object.entries(product.features)
    .filter(([_, isActive]) => isActive)
    .slice(0, 2)
    .map(([key]) => ICONS_MAP[key as keyof typeof ICONS_MAP]?.label)
    .filter(Boolean);

  // Use keywords or category for the blue subtitle
  const subCategory = product.keywords?.[0] || 'ACCESORIO';

  const badgeColor = getCategoryColor(product.category[0] || 'Ciudad');

  return (
    <div 
      onClick={() => onClick(product)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="group relative flex flex-col h-full bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 border border-gray-200 md:hover:border-[#459EDF] md:hover:shadow-2xl"
    >
      {/* Badge - Top Right */}
      <div className="absolute top-2 right-2 md:top-4 md:right-4 z-20 flex flex-col gap-1">
        {product.category.map((cat, idx) => (
          <span 
            key={idx}
            className="text-white text-[8px] md:text-[10px] font-bold px-2 py-1 md:px-3 md:py-1.5 uppercase rounded-sm tracking-wider md:tracking-widest shadow-sm"
            style={{ backgroundColor: getCategoryColor(cat) }}
          >
            {cat}
          </span>
        ))}
      </div>

      {/* Image Container - Square, Max Width 284px, Centered, Proportional */}
      <div className="w-full flex justify-center">
        <div className="relative aspect-square w-full max-w-[284px] mx-auto p-2 flex items-center justify-center bg-white overflow-hidden">
          {/* Image Wrapper */}
          <div className="relative w-full h-full">
            <img 
              src={product.images[currentImageIndex] || product.image} 
              alt={`${product.name} view ${currentImageIndex + 1}`}
              className="w-full h-full object-contain object-center transition-opacity duration-300"
            />
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className="px-4 pb-4 pt-2 md:px-6 md:pb-6 flex flex-col flex-grow relative">
        
        {/* Sub-Category */}
        <h4 className="text-[#459EDF] font-bold text-[10px] md:text-xs uppercase tracking-wider md:tracking-widest mb-2">
          {subCategory}
        </h4>

        {/* Title */}
        <h3 className="font-black italic text-lg md:text-xl lg:text-2xl text-[#061970] uppercase leading-none mb-3 md:mb-4 md:group-hover:text-[#FF7F32] transition-colors duration-300 line-clamp-2">
          {product.name}
        </h3>

        {/* Tags */}
        <div className="flex flex-col gap-1.5 md:gap-2 mt-auto">
          {activeFeatures.map((feature, idx) => (
            <span 
              key={idx} 
              className="inline-block bg-gray-100 text-gray-600 text-[9px] md:text-[10px] font-bold px-2.5 py-1 md:px-3 md:py-1.5 rounded uppercase tracking-wide w-fit"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Hover Action (Desktop Only) */}
        <div className="absolute bottom-6 right-6 hidden md:flex opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
            <div className="w-12 h-12 bg-[#FF7F32] flex items-center justify-center shadow-lg hover:bg-[#ff6b1a] transition-colors">
                <ArrowUpRight className="text-white w-6 h-6" />
            </div>
        </div>
      </div>
      
      {/* Bottom Gradient Line (Desktop Hover Only) */}
      <div className="hidden md:block absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#061970] to-[#FF7F32] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
};
